import { getSymbol, Input, Label, Modal, Select, SubmitButton, Table } from "@xola/ui-kit";
import { FormField, SecondaryButton } from "common";
import { round } from "lodash";
import React, { useEffect, useState } from "react";
import {
    Experience,
    LineItemCode,
    Package,
    PartnerPackageWithExpandedProducts,
    ProductCatalogItem,
    ProductObject,
    PurchaseRule,
    PurchaseRuleShortcutType,
} from "types";
import { array, number, object } from "yup";
import { ProductImage } from "../../Common/ProductImage";
import { XolaFormik } from "../../Common/XolaFormik";
import { DiscountHeader } from "./EditAddonPricingModal";
import {
    DemographicPricingFormValues,
    DiscountPricingValue,
    getCatalogByItem,
    getDefaultAction,
    getInitialDiscountType,
    getPackageDiscountAction,
    getPurchaseAction,
    getShortcutType,
    useLineItemTemplateByCode,
} from "./PricingSelectorHelper";
import { useSeller } from "@/hooks/useAccount";

interface EditDemographicPricingModalProps {
    demographics: ProductCatalogItem[];
    purchaseRule: PurchaseRule;
    experience: Experience;
    package: Package | PartnerPackageWithExpandedProducts;
    isOpen: boolean;
    onChange: (addonPricingValue: DiscountPricingValue[]) => void;
    onClose: () => void;
}

export const EditDemographicPricingModal = ({
    demographics,
    isOpen,
    purchaseRule,
    experience,
    package: pkg,
    onChange,
    onClose,
}: EditDemographicPricingModalProps) => {
    return (
        <Modal size="huge" isOpen={isOpen} onClose={onClose} onSubmit={onsubmit}>
            <ModalContent
                demographics={demographics}
                purchaseRule={purchaseRule}
                experience={experience}
                package={pkg}
                onChange={onChange}
                onClose={onClose}
            />
        </Modal>
    );
};

type EditPricingSelectorProps = Omit<EditDemographicPricingModalProps, "isOpen">;

const validationSchema = array(
    object({
        discount: number()
            .required("is required")
            .typeError("invalid number")
            .when("shortcutType", {
                is: PurchaseRuleShortcutType.PERCENT,
                then: number().max(100, "Discount should not be greater than 100"),
            }),
    }),
);

const ModalContent = ({
    demographics,
    purchaseRule,
    experience,
    package: pkg,
    onChange,
    onClose,
}: EditPricingSelectorProps) => {
    const title = "Edit Demographic Terms";
    const seller = useSeller();
    const { data: discountTemplate } = useLineItemTemplateByCode(
        pkg.object === ProductObject.PARTNER_PACKAGE
            ? LineItemCode.PARTNER_PACKAGE_DISCOUNT
            : LineItemCode.PACKAGE_DISCOUNT,
    );

    const handleFormSubmit = async (values: DemographicPricingFormValues[]) => {
        const discountPricingValues: DiscountPricingValue[] = [];

        for (const value of values) {
            const catalogItem = getCatalogByItem(demographics, value.catalogItem) ?? value.catalogItem;
            const purchaseAction = getPurchaseAction(purchaseRule, value.catalogItem.template?.id);
            const action = purchaseAction ?? getDefaultAction();
            const discountAction = getPackageDiscountAction(value, action, catalogItem, discountTemplate);
            discountPricingValues.push({
                action: discountAction,
            });
        }

        onChange(discountPricingValues);
    };

    const [discountType, setDiscountType] = useState<PurchaseRuleShortcutType | undefined>(undefined);
    const initialValues = getInitialValues(demographics, purchaseRule, discountType);

    useEffect(() => {
        setDiscountType(getInitialDiscountType(initialValues));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <XolaFormik
            enableReinitialize
            initialValues={initialValues}
            validationSchema={validationSchema}
            onSubmit={handleFormSubmit}
        >
            {({ handleSubmit, values, setFieldValue, errors }) => {
                return (
                    <>
                        <Modal.Header>{title}</Modal.Header>

                        <Modal.Body>
                            <div className="flex items-center space-x-1 pb-2">
                                <ProductImage product={experience} size="small" />
                                <Label>{experience.name}</Label>
                            </div>

                            <Table>
                                <Table.Head>
                                    <Table.Row>
                                        <Table.Header>Name</Table.Header>
                                        <Table.Header>
                                            <DiscountHeader
                                                discountType={discountType}
                                                setDiscountType={setDiscountType}
                                            />
                                        </Table.Header>
                                    </Table.Row>
                                </Table.Head>

                                <Table.Body isStriped>
                                    {values.map((value, index) => {
                                        return (
                                            <Table.Row key={value.catalogItem.id} className="justify-between">
                                                <Table.Cell className="w-1/5">{value.catalogItem.name}</Table.Cell>
                                                <Table.Cell className="w-1/3">
                                                    <FormField label="" name={`${index}.discount`}>
                                                        <div className="flex">
                                                            <Input
                                                                required
                                                                type="number"
                                                                isError={!!errors?.[index]?.discount ?? false}
                                                                className="w-full rounded-tr-none rounded-br-none border-gray-light"
                                                                value={value.discount}
                                                                onChange={(e: any) =>
                                                                    setFieldValue(
                                                                        `${index}.discount`,
                                                                        e.target.value
                                                                            ? Math.abs(Number(e.target.value))
                                                                            : undefined,
                                                                    )
                                                                }
                                                            />
                                                            <Select
                                                                className="w-28 rounded-none"
                                                                value={value.shortcutType}
                                                                disabled={discountType !== undefined}
                                                                onChange={(e: any) =>
                                                                    setFieldValue(
                                                                        `${index}.shortcutType`,
                                                                        e.target.value,
                                                                    )
                                                                }
                                                            >
                                                                <option
                                                                    value={PurchaseRuleShortcutType.ABSOLUTE_PER_UNIT}
                                                                >
                                                                    {getSymbol(seller.currency)} Off
                                                                </option>
                                                                <option value={PurchaseRuleShortcutType.PERCENT}>
                                                                    % Off
                                                                </option>
                                                                <option
                                                                    value={
                                                                        PurchaseRuleShortcutType.ABSOLUTE_FINAL_PRICE
                                                                    }
                                                                >
                                                                    Final Price
                                                                </option>
                                                            </Select>
                                                        </div>
                                                        {errors?.[index]?.discount ? (
                                                            <span className="text-sm font-bold text-danger">
                                                                {errors?.[index]?.discount}
                                                            </span>
                                                        ) : null}
                                                    </FormField>
                                                </Table.Cell>
                                            </Table.Row>
                                        );
                                    })}
                                </Table.Body>
                            </Table>
                        </Modal.Body>

                        <Modal.Footer>
                            <SecondaryButton onClick={onClose}>Cancel</SecondaryButton>
                            <SubmitButton type="submit" onClick={handleSubmit}>
                                Save
                            </SubmitButton>
                        </Modal.Footer>
                    </>
                );
            }}
        </XolaFormik>
    );
};

export const getInitialValues = (
    catalogs: ProductCatalogItem[],
    purchaseRule?: PurchaseRule,
    discountType?: PurchaseRuleShortcutType,
) => {
    const initialValues: DemographicPricingFormValues[] = [];

    for (const catalog of catalogs) {
        const purchaseAction = getPurchaseAction(purchaseRule, catalog.template?.id);
        const action = purchaseAction ?? getDefaultAction();
        const shortcutType = discountType ? discountType : getShortcutType(action);
        const discount = Math.abs(round(action.shortcut?.amount ?? 0, 2));

        initialValues.push({
            catalogItem: catalog,
            shortcutType: shortcutType,
            discount: discount,
        });
    }

    return initialValues;
};
